import { useEffect, useMemo, useState } from 'react';
import { Icone } from '../components/Icone';
import {
  BoutonMint,
  BoutonPrincipal,
  BoutonSecondaire,
  MessageErreur,
  Pagination,
} from '../components/ui/Primitives';
import { Modale } from '../components/ui/Modale';
import { useToast } from '../components/ui/useToast';
import { ErreurApi } from '../lib/api';
import {
  useConnexionGoogleCalendar,
  useCreerEvenementGoogle,
  useDeconnexionGoogleCalendar,
  useEtatGoogleCalendar,
  useEvenementsGoogleCalendar,
  useModifierEvenementGoogle,
  useSupprimerEvenementGoogle,
  type EntreeEvenementGoogle,
  type EvenementGoogle,
} from '../lib/useGoogleCalendar';
import {
  useAbonnementCalendrier,
  useCalendrierConsulte,
  useCreerEvenement,
  useEvenementsCalendrier,
  useModifierEvenement,
  usePersonnesCalendrier,
  usePlanningCalendrier,
  useSupprimerEvenement,
  type CriteresPlanning,
  type DetailPlanning,
  type EvenementPlanning,
  type PersonneCalendrier,
  type TypePersonne,
  type CategorieEvenement,
  type EvenementCalendrier as EvenementEnBase,
} from '../lib/useCalendrier';
import { TOUS_LES_SITES } from '../site/context';
import { usePerimetre } from '../site/usePerimetre';
import { ORDRE_TYPES, STYLES_JOUR, type TypeJour } from '../lib/calendrier-utils';
import { AbonnementAgenda } from '../components/calendrier/AbonnementAgenda';

/**
 * Calendrier de formation (écran `calendar` de la maquette validée,
 * `docs/maquette/modules/03-pages-dashboard-catalogue.js`).
 *
 * Trois sources cohabitent, et l'écran les distingue :
 * — le **planning de l'organisme**, lu sur `/calendrier/planning` : séances,
 *   rythme d'alternance, périodes en entreprise et échéances de dossier. Une
 *   seule route pour tous les profils — c'est le serveur qui décide du
 *   périmètre (`planning.service.ts`), pas cet écran. Ces lignes sont en
 *   lecture seule : le planning appartient à l'organisme ;
 * — les événements saisis à la main, conservés en base et rattachés au compte
 *   (`/calendrier/evenements`) ;
 * — Google Calendar, quand le compte est connecté (CDC §4.5).
 *
 * Dans l'autre sens, l'apprenant et le formateur abonnent leur agenda (Google,
 * Outlook, Apple) à leur planning par un lien iCal personnel (TF-452,
 * `AbonnementAgenda`) : l'API dit qui y a droit, le flux suit le planning.
 *
 * Aucune donnée d'exemple : un calendrier sans séance s'affiche vide.
 *
 * Les pôles administratif et pédagogique (et la direction) peuvent en outre
 * **consulter** le calendrier d'un apprenant ou d'un collègue de leur périmètre,
 * en lecture seule. L'API n'y expose que le planning de l'organisme : les
 * événements « Personnel » et le Google Calendar de la personne n'en sortent
 * jamais. L'écran ne propose ce sélecteur que si `consultationAutorisee` le dit.
 *
 * Les droits sur les événements saisis sont tenus par l'API, jamais par cet
 * écran : elle ne rend que ceux du compte appelant, et n'accepte de l'apprenant
 * que la catégorie « Personnel ». La liste `categoriesAutorisees` qu'elle
 * renvoie ne sert qu'à ne pas proposer un choix qui serait refusé.
 */

const JOURS = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'] as const;
const VUES = ['Jour', 'Semaine', 'Mois', 'Année'] as const;
const TAILLE_PAGE_ECHEANCES = 10 as const;
type Vue = (typeof VUES)[number];

type TonEvenement = 'mint' | 'blue' | 'warm' | 'perso' | 'google' | 'entreprise';
type CategorieCalendrier =
  | 'Mes formations'
  | 'En entreprise'
  | 'Réunions'
  | 'Examens / Émargements'
  | 'Personnel'
  | 'Google Calendar';

interface EvenementCalendrier {
  id: string;
  titre: string;
  categorie: CategorieCalendrier;
  /** Date civile locale (Indian/Reunion), format YYYY-MM-DD. */
  date: string;
  heureDebut?: string;
  heureFin?: string;
  lieu?: string;
  /** Affectation enregistrée dans « Programmation du calendrier ». */
  typeJour?: TypeJour;
  /**
   * Contexte d'une ligne venue du planning : formation, groupe, formateur,
   * antenne… Absent sur un événement saisi, qui n'a que ce que l'auteur a tapé.
   */
  detail?: DetailPlanning;
}

const BORDURE_PAR_CATEGORIE: Record<CategorieCalendrier, string> = {
  'Mes formations': 'border-codialis-mint',
  'En entreprise': 'border-codialis-blue',
  Réunions: 'border-codialis-sky',
  'Examens / Émargements': 'border-amber-500',
  Personnel: 'border-purple-500',
  'Google Calendar': 'border-emerald-500',
};

const TON_PAR_CATEGORIE: Record<CategorieCalendrier, TonEvenement> = {
  'Mes formations': 'mint',
  'En entreprise': 'entreprise',
  Réunions: 'blue',
  'Examens / Émargements': 'warm',
  Personnel: 'perso',
  'Google Calendar': 'google',
};

const TONS: Record<TonEvenement, string> = {
  mint: 'bg-codialis-mint/15 text-codialis-mint-clair',
  blue: 'bg-codialis-sky/15 text-codialis-sky',
  warm: 'bg-amber-100 text-amber-700',
  perso: 'bg-purple-100 text-purple-700',
  google: 'bg-emerald-100 text-emerald-700',
  entreprise: 'bg-codialis-blue/15 text-codialis-blue',
};

/**
 * Calendriers de la barre latérale : libellé, accent de la case, puce, et
 * visibilité par défaut. Tous visibles au premier affichage — une case décochée
 * d'origine faisait disparaître les événements que l'utilisateur venait de
 * créer dans cette catégorie, sans rien dire à l'écran.
 */
const CALENDRIERS: [CategorieCalendrier, string, string, boolean][] = [
  ['Mes formations', 'accent-codialis-mint', 'bg-codialis-mint', true],
  ['En entreprise', 'accent-codialis-blue', 'bg-codialis-blue', true],
  ['Réunions', 'accent-codialis-sky', 'bg-codialis-sky', true],
  ['Examens / Émargements', 'accent-amber-500', 'bg-amber-500', true],
  ['Personnel', 'accent-purple-500', 'bg-purple-500', true],
  ['Google Calendar', 'accent-emerald-500', 'bg-emerald-500', true],
];

/**
 * Catégories saisissables, côté API. « Google Calendar » n'en fait pas partie :
 * c'est une destination distincte, pas une catégorie de la base.
 */
const CATEGORIE_VERS_API: Record<
  Exclude<CategorieCalendrier, 'Google Calendar' | 'En entreprise'>,
  CategorieEvenement
> = {
  'Mes formations': 'MES_FORMATIONS',
  Réunions: 'REUNIONS',
  'Examens / Émargements': 'EXAMENS_EMARGEMENTS',
  Personnel: 'PERSONNEL',
};

const CATEGORIE_DEPUIS_API: Record<CategorieEvenement, CategorieCalendrier> = {
  MES_FORMATIONS: 'Mes formations',
  REUNIONS: 'Réunions',
  EXAMENS_EMARGEMENTS: 'Examens / Émargements',
  PERSONNEL: 'Personnel',
};

/** Événement de la base, rendu dans le vocabulaire de l'écran. */
function depuisBase(evenement: EvenementEnBase): EvenementCalendrier {
  return {
    id: evenement.id,
    titre: evenement.titre,
    categorie: CATEGORIE_DEPUIS_API[evenement.categorie],
    date: evenement.date,
    ...(evenement.heureDebut ? { heureDebut: evenement.heureDebut } : {}),
    ...(evenement.heureFin ? { heureFin: evenement.heureFin } : {}),
    ...(evenement.lieu ? { lieu: evenement.lieu } : {}),
  };
}

function pad(valeur: number): string {
  return String(valeur).padStart(2, '0');
}

/** Clé YYYY-MM-DD en date civile locale — jamais `toISOString`, qui bascule en UTC. */
function versCleJour(date: Date): string {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

function debutDuMois(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

function ajouterMois(date: Date, delta: number): Date {
  return new Date(date.getFullYear(), date.getMonth() + delta, 1);
}

function nombreJoursDuMois(date: Date): number {
  return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
}

/** Lundi = 0 … Dimanche = 6, alors que `getDay()` commence le dimanche. */
function decalageSemaine(date: Date): number {
  return (date.getDay() + 6) % 7;
}

/** Lundi de la semaine d'une date, à minuit local. */
function debutSemaine(date: Date): Date {
  const lundi = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  lundi.setDate(lundi.getDate() - decalageSemaine(lundi));
  return lundi;
}

function ajouterJoursLocaux(date: Date, jours: number): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate() + jours);
}

/**
 * Bornes civiles locales de la vue, bornes comprises. Ce sont elles qui
 * décident de la période chargée : changer de vue recharge le calendrier sur la
 * bonne fenêtre, il ne se contente pas de réafficher un mois.
 */
function bornesDeVue(vue: Vue, ancre: Date): { debut: Date; fin: Date } {
  switch (vue) {
    case 'Jour': {
      const jour = new Date(ancre.getFullYear(), ancre.getMonth(), ancre.getDate());
      return { debut: jour, fin: jour };
    }
    case 'Semaine': {
      const lundi = debutSemaine(ancre);
      return { debut: lundi, fin: ajouterJoursLocaux(lundi, 6) };
    }
    case 'Année':
      return {
        debut: new Date(ancre.getFullYear(), 0, 1),
        fin: new Date(ancre.getFullYear(), 11, 31),
      };
    default:
      return {
        debut: debutDuMois(ancre),
        fin: new Date(ancre.getFullYear(), ancre.getMonth() + 1, 0),
      };
  }
}

/** Déplacement d'une période entière, dans l'unité de la vue courante. */
function ajouterPeriode(vue: Vue, ancre: Date, delta: number): Date {
  switch (vue) {
    case 'Jour':
      return ajouterJoursLocaux(ancre, delta);
    case 'Semaine':
      return ajouterJoursLocaux(ancre, delta * 7);
    case 'Année':
      return new Date(ancre.getFullYear() + delta, ancre.getMonth(), 1);
    default:
      return ajouterMois(ancre, delta);
  }
}

function majuscule(texte: string): string {
  return texte.replace(/^./, (lettre) => lettre.toUpperCase());
}

/** Titre de la période affichée, dans le vocabulaire de la vue. */
function libelleDeVue(vue: Vue, ancre: Date): string {
  const { debut, fin } = bornesDeVue(vue, ancre);
  switch (vue) {
    case 'Jour':
      return majuscule(
        debut.toLocaleDateString('fr-FR', {
          weekday: 'long',
          day: 'numeric',
          month: 'long',
          year: 'numeric',
        }),
      );
    case 'Semaine': {
      const memeMois = debut.getMonth() === fin.getMonth();
      const gauche = debut.toLocaleDateString('fr-FR', {
        day: 'numeric',
        ...(memeMois ? {} : { month: 'long' }),
      });
      const droite = fin.toLocaleDateString('fr-FR', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      });
      return `${gauche} – ${droite}`;
    }
    case 'Année':
      return String(ancre.getFullYear());
    default:
      return majuscule(ancre.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' }));
  }
}

/** Écart en jours calendaires pleins entre deux clés YYYY-MM-DD, sans passer par les millisecondes. */
function differenceJoursCivils(depuis: string, vers: string): number {
  const [ya, ma, da] = depuis.split('-').map(Number) as [number, number, number];
  const [yb, mb, db] = vers.split('-').map(Number) as [number, number, number];
  const a = Date.UTC(ya, ma - 1, da);
  const b = Date.UTC(yb, mb - 1, db);
  return Math.round((b - a) / 86_400_000);
}

/** Préfixe des événements venus du dossier : ils se lisent, ils ne s'éditent pas. */
const PREFIXE_PLATEFORME = 'planning:';

function estPlateforme(id: string): boolean {
  return id.startsWith(PREFIXE_PLATEFORME);
}

/**
 * Case de filtre d'une ligne de planning. La nature vient du serveur, le
 * regroupement est un choix d'affichage : une journée d'alternance au centre se
 * range avec les séances, une journée en entreprise avec les immersions.
 */
const CATEGORIE_PAR_NATURE: Record<EvenementPlanning['nature'], CategorieCalendrier> = {
  SEANCE: 'Mes formations',
  PROGRAMMATION: 'Mes formations',
  ALTERNANCE_CENTRE: 'Mes formations',
  ECHEANCE: 'Examens / Émargements',
  ALTERNANCE_ENTREPRISE: 'En entreprise',
  IMMERSION: 'En entreprise',
  ENTRETIEN: 'Réunions',
};

/**
 * Planning de l'organisme, rendu dans le vocabulaire de l'écran. Il est en
 * lecture seule quel que soit le profil : ces lignes viennent des séances, du
 * rythme d'alternance, des immersions et des échéances de dossier, qui se
 * tiennent dans leurs écrans respectifs et pas dans un calendrier.
 */
function versEvenementsPlateforme(evenements: EvenementPlanning[]): EvenementCalendrier[] {
  return evenements.map((evenement) => ({
    id: evenement.id,
    titre: evenement.titre,
    categorie: CATEGORIE_PAR_NATURE[evenement.nature],
    date: evenement.date,
    ...(evenement.heureDebut ? { heureDebut: evenement.heureDebut } : {}),
    ...(evenement.heureFin ? { heureFin: evenement.heureFin } : {}),
    ...(evenement.lieu ? { lieu: evenement.lieu } : {}),
    ...(evenement.typeJour ? { typeJour: evenement.typeJour } : {}),
    detail: evenement.detail,
  }));
}

/** Style partagé avec `/adm-formations` pour les journées programmées. */
function classeEvenement(evenement: EvenementCalendrier): string {
  return evenement.typeJour
    ? STYLES_JOUR[evenement.typeJour].pastille
    : TONS[TON_PAR_CATEGORIE[evenement.categorie]];
}

function libelleCalendrier(evenement: EvenementCalendrier): string {
  return evenement.typeJour
    ? STYLES_JOUR[evenement.typeJour].libelle
    : evenement.categorie;
}

/** Heure civile locale (Indian/Reunion) d'un horodatage, pour la dernière synchro. */
function heureLocale(instant: number): string {
  return new Intl.DateTimeFormat('fr-FR', {
    timeZone: 'Indian/Reunion',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(instant));
}

/** Événement cible construit en heure locale Indian/Reunion (UTC+4). */
function instantLocal(date: string, heure: string): Date {
  return new Date(`${date}T${heure}:00+04:00`);
}

/**
 * Adaptateurs Google Calendar ↔ forme locale. L'API renvoie déjà des
 * horodatages en heure civile locale (`timeZone: 'Indian/Reunion'` posé côté
 * serveur) : on découpe la chaîne, on ne repasse jamais par un `Date` UTC.
 */
function depuisGoogle(evenement: EvenementGoogle): EvenementCalendrier {
  return {
    id: `google:${evenement.id}`,
    titre: evenement.titre,
    categorie: 'Google Calendar',
    date: evenement.journeeEntiere ? evenement.debut : evenement.debut.slice(0, 10),
    ...(evenement.journeeEntiere ? {} : { heureDebut: evenement.debut.slice(11, 16) }),
    ...(evenement.journeeEntiere ? {} : { heureFin: evenement.fin.slice(11, 16) }),
    ...(evenement.lieu ? { lieu: evenement.lieu } : {}),
  };
}

/** Lendemain d'une date civile `YYYY-MM-DD`, sans jamais repasser par UTC. */
function jourSuivant(date: string): string {
  const [annee, mois, jour] = date.split('-').map(Number) as [number, number, number];
  const suivant = new Date(annee, mois - 1, jour + 1);
  return versCleJour(suivant);
}

function versGoogle(entree: Omit<EvenementCalendrier, 'id'>): EntreeEvenementGoogle {
  const journeeEntiere = !entree.heureDebut;

  if (journeeEntiere) {
    // Google veut une fin *exclusive* pour une journée entière : un événement
    // d'un jour finit le lendemain. Envoyer la même date des deux côtés décrit
    // un intervalle vide, que l'API rejette en 400 — c'est ce qui faisait
    // échouer toute modification d'un événement sans horaire.
    return {
      titre: entree.titre,
      ...(entree.lieu ? { lieu: entree.lieu } : {}),
      journeeEntiere,
      debut: entree.date,
      fin: jourSuivant(entree.date),
    };
  }

  // Fin absente ou antérieure au début (saisie d'un créneau franchissant
  // minuit) : l'événement se termine le lendemain, sinon Google refuse.
  const heureFin = entree.heureFin || entree.heureDebut!;
  const dateFin = heureFin < entree.heureDebut! ? jourSuivant(entree.date) : entree.date;
  return {
    titre: entree.titre,
    ...(entree.lieu ? { lieu: entree.lieu } : {}),
    journeeEntiere,
    debut: `${entree.date}T${entree.heureDebut}:00`,
    fin: `${dateFin}T${heureFin}:00`,
  };
}

/** Un événement venu de Google, reconnaissable au préfixe posé par `depuisGoogle`. */
function estGoogle(id: string): boolean {
  return id.startsWith('google:');
}

/** Identifiant Google d'un événement affiché (préfixe `google:` retiré). */
function idGoogle(id: string): string {
  return id.slice('google:'.length);
}

function libelleEcheance(evenement: EvenementCalendrier, maintenant: Date): string {
  const aujourdHui = versCleJour(maintenant);

  if (evenement.date === aujourdHui) {
    if (!evenement.heureDebut) return "Aujourd'hui";

    const debut = instantLocal(evenement.date, evenement.heureDebut);
    const diffMs = debut.getTime() - maintenant.getTime();
    if (diffMs > 0) {
      const minutes = Math.round(diffMs / 60_000);
      const heures = Math.floor(minutes / 60);
      const reste = minutes % 60;
      return heures > 0 ? `Dans ${heures} h ${pad(reste)} min` : `Dans ${minutes} min`;
    }

    if (evenement.heureFin) {
      const fin = instantLocal(evenement.date, evenement.heureFin);
      if (maintenant.getTime() <= fin.getTime()) return 'En cours';
    }
    return 'Terminé';
  }

  const diffJours = differenceJoursCivils(aujourdHui, evenement.date);
  if (diffJours > 0) return `Dans ${diffJours} jour${diffJours > 1 ? 's' : ''}`;
  return 'Passé';
}

/** Échappe `,`, `;` et les retours à la ligne, seuls caractères qui casseraient le format .ics. */
function echapperIcs(texte: string): string {
  return texte.replace(/([,;])/g, '\\$1').replace(/\n/g, '\\n');
}

function versHorodatageIcs(instant: Date): string {
  return `${instant.getUTCFullYear()}${pad(instant.getUTCMonth() + 1)}${pad(instant.getUTCDate())}T${pad(
    instant.getUTCHours(),
  )}${pad(instant.getUTCMinutes())}00Z`;
}

/**
 * Export iCal (RFC 5545) — aucune dépendance à Google/Microsoft : ces SSO sont
 * maquettés mais non contractualisés (voir CLAUDE.md). Le fichier .ics
 * s'importe manuellement dans n'importe quel client de calendrier.
 */
function genererIcs(evenements: EvenementCalendrier[]): string {
  const lignes = ['BEGIN:VCALENDAR', 'VERSION:2.0', 'PRODID:-//Top Formation//Calendrier//FR'];

  for (const evenement of evenements) {
    lignes.push('BEGIN:VEVENT', `UID:${evenement.id}@calendrier.topformation.re`);
    if (evenement.heureDebut) {
      const debut = instantLocal(evenement.date, evenement.heureDebut);
      const fin = evenement.heureFin
        ? instantLocal(evenement.date, evenement.heureFin)
        : new Date(debut.getTime() + 60 * 60 * 1000);
      lignes.push(`DTSTART:${versHorodatageIcs(debut)}`, `DTEND:${versHorodatageIcs(fin)}`);
    } else {
      const [annee, mois, jour] = evenement.date.split('-').map(Number) as [number, number, number];
      const lendemain = new Date(Date.UTC(annee, mois - 1, jour + 1));
      lignes.push(
        `DTSTART;VALUE=DATE:${evenement.date.replace(/-/g, '')}`,
        `DTEND;VALUE=DATE:${lendemain.getUTCFullYear()}${pad(lendemain.getUTCMonth() + 1)}${pad(lendemain.getUTCDate())}`,
      );
    }
    lignes.push(`SUMMARY:${echapperIcs(evenement.titre)}`, `CATEGORIES:${echapperIcs(evenement.categorie)}`);
    if (evenement.lieu) lignes.push(`LOCATION:${echapperIcs(evenement.lieu)}`);
    lignes.push('END:VEVENT');
  }

  lignes.push('END:VCALENDAR');
  return lignes.join('\r\n');
}

function telechargerIcs(contenu: string): void {
  const blob = new Blob([contenu], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const lien = document.createElement('a');
  lien.href = url;
  lien.download = 'calendrier-top-formation.ics';
  document.body.appendChild(lien);
  lien.click();
  document.body.removeChild(lien);
  URL.revokeObjectURL(url);
}

/**
 * Cases de filtre cochées, conservées dans le navigateur.
 *
 * Seule préférence d'affichage qui reste locale : elle n'engage que ce poste et
 * ne mérite pas un aller-retour serveur. Les événements, eux, sont en base
 * depuis le module `calendrier` — c'est là que vivent leurs droits.
 *
 * `CLE_EVENEMENTS` n'est plus écrite : elle n'est lue que pour effacer ce
 * qu'une version antérieure avait laissé dans les navigateurs. Une ancienne
 * version y semait des événements d'exemple repris de la maquette, qui
 * survivaient à leur suppression du code.
 */
// v2 : la version précédente enregistrait « Personnel » décoché par défaut.
// Les navigateurs qui ont chargé cette ancienne version gardaient donc ce
// filtre éternellement décoché malgré le nouveau défaut à `true` — la clé
// change pour que ce résidu ne soit plus relu.
const CLE_FILTRES = 'topformation.calendrier.filtres.v2';
const CLE_FILTRES_OBSOLETE = 'topformation.calendrier.filtres';
const CLE_EVENEMENTS = 'topformation.calendrier.evenements';

function lireStockage<T>(cle: string, defaut: T): T {
  try {
    const brut = window.localStorage.getItem(cle);
    return brut ? (JSON.parse(brut) as T) : defaut;
  } catch {
    // Stockage indisponible (navigation privée, quota) : l'écran fonctionne
    // à l'identique, sans mémoire d'une session à l'autre.
    return defaut;
  }
}

function ecrireStockage(cle: string, valeur: unknown): void {
  try {
    window.localStorage.setItem(cle, JSON.stringify(valeur));
  } catch {
    /* Sans stockage, on n'empêche pas l'utilisateur de travailler. */
  }
}

function filtresParDefaut(): Record<CategorieCalendrier, boolean> {
  return Object.fromEntries(
    CALENDRIERS.map(([libelle, , , actifParDefaut]) => [libelle, actifParDefaut]),
  ) as Record<CategorieCalendrier, boolean>;
}

function filtresTypesJoursParDefaut(): Record<TypeJour, boolean> {
  return Object.fromEntries(ORDRE_TYPES.map((type) => [type, true])) as Record<TypeJour, boolean>;
}

/** Filtres relus du stockage, complétés par les défauts pour toute catégorie ajoutée depuis. */
function filtresInitiaux(): Record<CategorieCalendrier, boolean> {
  const defauts = filtresParDefaut();
  const enregistres = lireStockage<Partial<Record<CategorieCalendrier, boolean>>>(
    CLE_FILTRES,
    {},
  );
  for (const [libelle, valeur] of Object.entries(enregistres)) {
    if (libelle in defauts && typeof valeur === 'boolean') {
      defauts[libelle as CategorieCalendrier] = valeur;
    }
  }
  return defauts;
}

/**
 * Purge du stockage laissé par les versions qui gardaient les événements dans
 * le navigateur. Sans elle, les événements d'exemple d'une ancienne version
 * restent affichés indéfiniment sur les postes qui l'ont chargée, sans plus
 * exister nulle part dans le code.
 */
function purgerEvenementsLocaux(): void {
  try {
    window.localStorage.removeItem(CLE_EVENEMENTS);
    window.localStorage.removeItem(CLE_FILTRES_OBSOLETE);
  } catch {
    /* Stockage indisponible : il n'y a alors rien à purger. */
  }
}

type EtatModale =
  | { mode: 'creation'; date: string }
  | { mode: 'edition'; evenement: EvenementCalendrier }
  /** Lecture seule : planning de l'organisme, ou calendrier d'un tiers. */
  | { mode: 'detail'; evenement: EvenementCalendrier }
  | null;

export function CalendrierPage(): JSX.Element {
  const notifier = useToast();

  // Une seule date d'ancrage pour les quatre vues : c'est `bornesDeVue` qui en
  // tire la fenêtre à charger, et `ajouterPeriode` qui la déplace d'une unité
  // de la vue courante.
  const [vue, setVue] = useState<Vue>('Mois');
  const [ancre, setAncre] = useState(() => new Date());
  const [actifs, setActifs] = useState<Record<CategorieCalendrier, boolean>>(filtresInitiaux);
  const [typesJoursActifs, setTypesJoursActifs] = useState<Record<TypeJour, boolean>>(
    filtresTypesJoursParDefaut,
  );
  const [pageEcheances, setPageEcheances] = useState(1);

  useEffect(() => ecrireStockage(CLE_FILTRES, actifs), [actifs]);
  useEffect(() => purgerEvenementsLocaux(), []);
  const [modale, setModale] = useState<EtatModale>(null);
  const [maintenant, setMaintenant] = useState(() => new Date());

  // Rafraîchit les échéances « dans X min » sans attendre une interaction.
  useEffect(() => {
    const intervalle = window.setInterval(() => setMaintenant(new Date()), 30_000);
    return () => window.clearInterval(intervalle);
  }, []);

  // Retour du flux OAuth (redirection serveur vers `?google=connecte|erreur`).
  useEffect(() => {
    const parametres = new URLSearchParams(window.location.search);
    const resultat = parametres.get('google');
    if (!resultat) return;
    notifier(
      resultat === 'connecte'
        ? 'Google Calendar connecté'
        : resultat === 'session'
          ? 'Session expirée pendant la connexion Google : reconnectez-vous, puis réessayez'
          : 'La connexion Google Calendar a échoué',
    );
    window.history.replaceState(null, '', window.location.pathname);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const bornes = useMemo(() => bornesDeVue(vue, ancre), [vue, ancre]);
  const debutPeriodeCle = versCleJour(bornes.debut);
  const finPeriodeCle = versCleJour(bornes.fin);

  // Filtres des profils à vue étendue. Leurs options viennent de l'API, jamais
  // d'une liste écrite ici : une antenne créée dans `/adm-antennes` apparaît
  // sans toucher à cet écran.
  const [criteres, setCriteres] = useState<CriteresPlanning>({
    site: '',
    formation: '',
    groupe: '',
    formateur: '',
  });

  // Planning de l'organisme : une seule route pour tous les profils, le
  // périmètre étant décidé côté serveur d'après le profil connecté.
  const planning = usePlanningCalendrier(debutPeriodeCle, finPeriodeCle, criteres);
  // Événements saisis à la main, désormais en base et rattachés au compte.
  const evenementsSaisis = useEvenementsCalendrier(debutPeriodeCle, finPeriodeCle);
  const creerEvenementEnBase = useCreerEvenement();
  const modifierEvenementEnBase = useModifierEvenement();
  const supprimerEvenementEnBase = useSupprimerEvenement();

  // Catégories que l'API accepte de ce profil. Tant qu'elle n'a pas répondu, on
  // n'en propose aucune plutôt que d'en proposer une qu'elle refuserait.
  const categoriesSaisissables = useMemo(
    () =>
      (evenementsSaisis.data?.categoriesAutorisees ?? []).map(
        (categorie) => CATEGORIE_DEPUIS_API[categorie],
      ),
    [evenementsSaisis.data?.categoriesAutorisees],
  );

  // Consultation du calendrier d'un tiers. `cible` à `null` = son propre
  // calendrier, seul cas où l'écran écrit quoi que ce soit.
  const [cible, setCible] = useState<PersonneCalendrier | null>(null);
  const droitConsultation = evenementsSaisis.data?.consultationAutorisee;
  // Un changement de période relance la requête et rend momentanément son
  // résultat indéfini. Tant qu'un calendrier tiers est déjà ouvert, cet état
  // signifie « vérification en cours », pas « droit retiré ».
  const consultationAutorisee =
    droitConsultation === true || (cible !== null && droitConsultation === undefined);
  const [rechercheCible, setRechercheCible] = useState('');
  const [selecteurOuvert, setSelecteurOuvert] = useState(false);
  // Bascule de vue de l'annuaire : apprenants, équipe, ou les deux. Le site
  // vient du périmètre déjà choisi en haut de l'application — pas un second
  // sélecteur qui pourrait le contredire.
  const [vueAnnuaire, setVueAnnuaire] = useState<TypePersonne | null>(null);
  const { site: sitePerimetre } = usePerimetre();

  // Le sélecteur de périmètre de l'application fait foi : changer d'antenne en
  // haut de l'écran recadre le planning sans qu'on ait à le redire ici.
  useEffect(() => {
    const code = sitePerimetre === TOUS_LES_SITES ? '' : sitePerimetre;
    setCriteres((valeurs) => (valeurs.site === code ? valeurs : { ...valeurs, site: code }));
  }, [sitePerimetre]);
  const personnes = usePersonnesCalendrier(
    {
      recherche: rechercheCible,
      type: vueAnnuaire,
      site: sitePerimetre === TOUS_LES_SITES ? '' : sitePerimetre,
    },
    consultationAutorisee && selecteurOuvert,
  );
  const calendrierConsulte = useCalendrierConsulte(cible, debutPeriodeCle, finPeriodeCle);
  const enConsultation = cible !== null;

  // Un profil qui perdrait le droit en cours de session ne doit pas rester
  // devant le calendrier d'un tiers. Seul un refus explicite reçu avec succès
  // ferme la consultation ; le chargement d'une autre période ne le fait pas.
  useEffect(() => {
    if (evenementsSaisis.isSuccess && droitConsultation === false && cible) setCible(null);
  }, [evenementsSaisis.isSuccess, droitConsultation, cible]);

  // Abonnement de l'agenda personnel (TF-452). L'API répond par un refus aux
  // profils sans planning personnel : le bouton ne leur est pas proposé.
  const abonnementAgenda = useAbonnementCalendrier();
  const abonnementPossible = abonnementAgenda.data !== undefined && abonnementAgenda.data.refus === null;
  const abonnementActif = abonnementPossible && abonnementAgenda.data?.abonnement != null;
  const [abonnementOuvert, setAbonnementOuvert] = useState(false);

  const etatGoogle = useEtatGoogleCalendar();
  const connexionGoogle = useConnexionGoogleCalendar();
  const deconnexionGoogle = useDeconnexionGoogleCalendar();
  const creerGoogle = useCreerEvenementGoogle();
  const modifierGoogle = useModifierEvenementGoogle();
  const supprimerGoogle = useSupprimerEvenementGoogle();
  const connecteGoogle = etatGoogle.data?.connecte === true;

  // Bornes du mois affiché, en heure civile locale (Indian/Reunion) — jamais
  // un instant UTC, sur le même principe que le reste de l'écran.
  const depuisGoogleIso = instantLocal(debutPeriodeCle, '00:00').toISOString();
  const jusquaGoogleIso = instantLocal(finPeriodeCle, '23:59').toISOString();
  const evenementsGoogle = useEvenementsGoogleCalendar(depuisGoogleIso, jusquaGoogleIso, connecteGoogle);

  // La synchronisation est automatique (voir `PERIODE_SYNCHRO_MS` dans
  // `useGoogleCalendar`) : changement de mois, retour sur l'onglet, et sondage
  // périodique. Ce bouton ne sert donc qu'à forcer l'instant présent — après
  // un ajout fait à l'instant dans l'agenda Google, typiquement — et à dire
  // ce qui a été trouvé, ce qu'un rafraîchissement silencieux ne fait pas.
  const synchroniserGoogle = async (): Promise<void> => {
    const [etat, evenements] = await Promise.all([etatGoogle.refetch(), evenementsGoogle.refetch()]);
    if (etat.data?.connecte === false) {
      notifier('Compte Google déconnecté : reconnectez-le pour synchroniser');
      return;
    }
    if (etat.isError || evenements.isError) {
      notifier(
        messageErreurGoogle(
          evenements.error ?? etat.error,
          'Synchronisation Google Calendar impossible',
        ),
      );
      return;
    }
    const nombre = evenements.data?.length ?? 0;
    notifier(
      nombre === 0
        ? 'Google Calendar synchronisé : aucun événement sur ce mois'
        : `Google Calendar synchronisé : ${nombre} événement${nombre > 1 ? 's' : ''} sur ce mois`,
    );
  };
  const synchroEnCours = evenementsGoogle.isFetching || etatGoogle.isFetching;

  // Connexion OAuth : la mutation quitte la page (redirection vers Google).
  // Si elle échoue avant — identifiants absents côté serveur, notamment — rien
  // ne se passe à l'écran : l'échec doit être dit, pas avalé.
  const connecterGoogle = (): void => {
    connexionGoogle.mutate(undefined, {
      onError: (erreur) =>
        notifier(messageErreurGoogle(erreur, 'Connexion à Google Calendar impossible')),
    });
  };

  /**
   * Ouvre l'événement à l'édition, sauf s'il vient du dossier : ces séances
   * sont planifiées par l'organisme et l'apprenant ne peut pas les déplacer.
   * Le dire vaut mieux qu'un clic sans effet.
   *
   * Même règle pour un événement dont la catégorie n'est plus ouverte au
   * profil : c'est le cas d'un apprenant devant une ligne « Examens » héritée
   * d'une version qui le laissait s'en créer. L'API la refuserait de toute
   * façon — l'écran le dit au lieu d'ouvrir un formulaire qui échouera.
   */
  const ouvrirEvenement = (evenement: EvenementCalendrier) => {
    // Lecture seule : on ouvre le détail plutôt que de refuser d'un toast. Un
    // clic sans effet sur une séance laissait l'utilisateur sans les
    // informations qu'il venait justement chercher — formateur, groupe, salle.
    if (enConsultation || estPlateforme(evenement.id)) {
      setModale({ mode: 'detail', evenement });
      return;
    }
    if (
      !estGoogle(evenement.id) &&
      categoriesSaisissables.length > 0 &&
      !categoriesSaisissables.includes(evenement.categorie)
    ) {
      notifier('Cet événement relève du planning de l’organisme : il ne se modifie pas ici');
      return;
    }
    setModale({ mode: 'edition', evenement });
  };

  /**
   * Ouverture du formulaire depuis une case de grille. `null` en consultation :
   * on ne crée rien dans le calendrier de quelqu'un d'autre, et la vue le sait
   * plutôt que d'afficher des zones cliquables sans effet.
   */
  const creerDepuisGrille = enConsultation
    ? null
    : (date: string) => setModale({ mode: 'creation', date });

  const basculer = (categorie: CategorieCalendrier) =>
    setActifs((valeurs) => ({ ...valeurs, [categorie]: !valeurs[categorie] }));

  const basculerTypeJour = (type: TypeJour) =>
    setTypesJoursActifs((valeurs) => ({ ...valeurs, [type]: !valeurs[type] }));

  const ajourdHuiCle = versCleJour(new Date());


  // Les deux sources cohabitent : connecter Google *ajoute* un calendrier, il
  // ne remplace pas celui de la plateforme. Aucune ambiguïté à l'écran — la
  // catégorie « Google Calendar » les distingue, sa case les masque, et le
  // préfixe `google:` de l'identifiant dit à quelle source écrire.
  // Un refus ou une panne sur le dossier ne doit pas passer pour un mois vide :
  // le calendrier afficherait un planning inexistant sans rien dire.
  const erreurPlateforme = planning.isError
    ? planning.error instanceof ErreurApi
      ? planning.error.message
      : 'Le planning n’a pas pu être chargé.'
    : null;

  // Même raison pour les événements saisis : un mois vide parce que l'appel a
  // échoué se lit comme un mois sans rien de prévu.
  const erreurEvenements = evenementsSaisis.isError
    ? evenementsSaisis.error instanceof ErreurApi
      ? evenementsSaisis.error.message
      : 'Vos événements n’ont pas pu être chargés.'
    : null;

  // Même raison encore : un calendrier consulté qui échoue ne doit pas passer
  // pour un planning vide chez la personne qu'on vérifie.
  const erreurConsultation =
    enConsultation && calendrierConsulte.isError
      ? calendrierConsulte.error instanceof ErreurApi
        ? calendrierConsulte.error.message
        : 'Ce calendrier n’a pas pu être chargé.'
      : null;

  const evenementsPlateforme = useMemo(
    () => versEvenementsPlateforme(planning.data?.evenements ?? []),
    [planning.data?.evenements],
  );

  const evenements = useMemo(
    () => (evenementsSaisis.data?.evenements ?? []).map(depuisBase),
    [evenementsSaisis.data?.evenements],
  );

  /**
   * Événements du tiers consulté. Le préfixe `consulte:` les distingue à coup
   * sûr des siens : aucune écriture ne doit partir sur l'identifiant d'un
   * événement qui appartient à quelqu'un d'autre.
   */
  const evenementsConsultes = useMemo(
    () =>
      (calendrierConsulte.data?.evenements ?? []).map((evenement) => ({
        id: `consulte:${evenement.id}`,
        titre: evenement.titre,
        categorie: CATEGORIE_DEPUIS_API[evenement.categorie],
        date: evenement.date,
        ...(evenement.heureDebut ? { heureDebut: evenement.heureDebut } : {}),
        ...(evenement.heureFin ? { heureFin: evenement.heureFin } : {}),
        ...(evenement.lieu ? { lieu: evenement.lieu } : {}),
        ...(evenement.typeJour ? { typeJour: evenement.typeJour } : {}),
      })),
    [calendrierConsulte.data?.evenements],
  );

  const evenementsAffiches = useMemo(
    () =>
      enConsultation
        ? evenementsConsultes
        : connecteGoogle
        ? [
            ...evenementsPlateforme,
            ...evenements,
            ...(evenementsGoogle.data ?? []).map(depuisGoogle),
          ]
        : [...evenementsPlateforme, ...evenements],
    [
      connecteGoogle,
      enConsultation,
      evenementsConsultes,
      evenementsGoogle.data,
      evenements,
      evenementsPlateforme,
    ],
  );

  /**
   * Deux jeux de cases, jamais l'un à la place de l'autre : les affectations de
   * la programmation d'un côté, les calendriers de l'autre. Une journée
   * programmée porte toujours un `typeJour` et ne dépend donc que de sa
   * pastille ; tout le reste ne dépend que de sa catégorie. Aucun profil
   * n'échappe au filtrage — auparavant, sur un calendrier apprenant, décocher
   * « Mes formations » ne masquait rien, la condition court-circuitant la case.
   */
  const estVisible = (evenement: EvenementCalendrier): boolean =>
    evenement.typeJour ? typesJoursActifs[evenement.typeJour] : actifs[evenement.categorie];

  /**
   * Calendrier rattaché à un dossier : le sien, ou celui d'un apprenant
   * consulté. Seul cas où l'absence de programmation se dit — ailleurs, elle
   * n'est pas une anomalie, la journée programmée n'est pas un objet d'antenne.
   */
  const calendrierApprenantAffiche =
    (!enConsultation && planning.data?.perimetre === 'PERSONNEL') ||
    (enConsultation && cible?.type === 'APPRENANT');

  /** Volume par affectation sur la période affichée, annoncé par chaque pastille. */
  const compteTypesJours = useMemo(() => {
    const compte = Object.fromEntries(ORDRE_TYPES.map((type) => [type, 0])) as Record<
      TypeJour,
      number
    >;
    for (const evenement of evenementsAffiches) {
      if (evenement.typeJour) compte[evenement.typeJour] += 1;
    }
    return compte;
  }, [evenementsAffiches]);

  const programmationPresente = ORDRE_TYPES.some((type) => compteTypesJours[type] > 0);

  const evenementsParJour = useMemo(() => {
    const table = new Map<string, EvenementCalendrier[]>();
    for (const evenement of evenementsAffiches) {
      if (!estVisible(evenement)) continue;
      const liste = table.get(evenement.date) ?? [];
      liste.push(evenement);
      table.set(evenement.date, liste);
    }
    return table;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [evenementsAffiches, actifs, typesJoursActifs]);

  const evenementsAujourdHui = useMemo(
    () =>
      (evenementsParJour.get(ajourdHuiCle) ?? [])
        .slice()
        .sort((a, b) => (a.heureDebut ?? '').localeCompare(b.heureDebut ?? '')),
    [evenementsParJour, ajourdHuiCle],
  );

  const prochainesEcheances = useMemo(
    () =>
      evenementsAffiches
        .filter(
          (evenement) =>
            estVisible(evenement) && differenceJoursCivils(ajourdHuiCle, evenement.date) >= 0,
        )
        .sort((a, b) => {
          const dates = a.date.localeCompare(b.date);
          return dates !== 0 ? dates : (a.heureDebut ?? '').localeCompare(b.heureDebut ?? '');
        }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [evenementsAffiches, actifs, ajourdHuiCle, typesJoursActifs],
  );
  const pagesEcheances = Math.max(
    1,
    Math.ceil(prochainesEcheances.length / TAILLE_PAGE_ECHEANCES),
  );
  const pageEcheancesCourante = Math.min(pageEcheances, pagesEcheances);
  const prochainesEcheancesPage = useMemo(
    () =>
      prochainesEcheances.slice(
        (pageEcheancesCourante - 1) * TAILLE_PAGE_ECHEANCES,
        pageEcheancesCourante * TAILLE_PAGE_ECHEANCES,
      ),
    [prochainesEcheances, pageEcheancesCourante],
  );

  // Une nouvelle période, un autre apprenant ou un filtre différent repart de
  // la première échéance au lieu de conserver un numéro de page devenu hors
  // contexte.
  useEffect(() => {
    setPageEcheances(1);
  }, [debutPeriodeCle, finPeriodeCle, cible?.id, cible?.type, actifs, criteres, typesJoursActifs]);

  function messageErreurGoogle(erreur: unknown, repli: string): string {
    return erreur instanceof ErreurApi ? erreur.message : repli;
  }

  /** Entrée de l'écran, traduite pour l'API des événements saisis. */
  function versEntreeApi(entree: Omit<EvenementCalendrier, 'id'>) {
    if (entree.categorie === 'Google Calendar' || entree.categorie === 'En entreprise') {
      throw new Error('Cette catégorie ne peut pas être enregistrée dans la base');
    }
    return {
      titre: entree.titre,
      categorie: CATEGORIE_VERS_API[entree.categorie],
      date: entree.date,
      heureDebut: entree.heureDebut ?? null,
      heureFin: entree.heureFin ?? null,
      lieu: entree.lieu ?? null,
    };
  }

  const ajouterEvenement = (entree: Omit<EvenementCalendrier, 'id'>) => {
    // Le calendrier choisi dans le formulaire décide de la destination.
    if (entree.categorie === 'Google Calendar') {
      creerGoogle.mutate(versGoogle(entree), {
        onSuccess: () => {
          setActifs((valeurs) =>
            valeurs['Google Calendar'] ? valeurs : { ...valeurs, 'Google Calendar': true },
          );
          setModale(null);
          notifier('Événement ajouté à Google Calendar');
        },
        onError: (erreur) => notifier(messageErreurGoogle(erreur, "Impossible d'ajouter l'événement")),
      });
      return;
    }
    creerEvenementEnBase.mutate(versEntreeApi(entree), {
      onSuccess: () => {
        // Un événement ne naît jamais invisible : si la case de son calendrier
        // est décochée, on la recoche plutôt que de laisser l'utilisateur
        // devant un écran inchangé alors que l'enregistrement a bien eu lieu.
        setActifs((valeurs) =>
          valeurs[entree.categorie] ? valeurs : { ...valeurs, [entree.categorie]: true },
        );
        setModale(null);
        notifier('Événement ajouté');
      },
      onError: (erreur) => notifier(messageErreurGoogle(erreur, "Impossible d'ajouter l'événement")),
    });
  };

  const modifierEvenement = (id: string, entree: Omit<EvenementCalendrier, 'id'>) => {
    if (estGoogle(id)) {
      modifierGoogle.mutate(
        { id: idGoogle(id), entree: versGoogle(entree) },
        {
          onSuccess: () => {
            setModale(null);
            notifier('Événement modifié sur Google Calendar');
          },
          onError: (erreur) => notifier(messageErreurGoogle(erreur, "Impossible de modifier l'événement")),
        },
      );
      return;
    }
    modifierEvenementEnBase.mutate(
      { id, entree: versEntreeApi(entree) },
      {
        onSuccess: () => {
          setModale(null);
          notifier('Événement modifié');
        },
        onError: (erreur) =>
          notifier(messageErreurGoogle(erreur, "Impossible de modifier l'événement")),
      },
    );
  };

  const supprimerEvenement = (id: string) => {
    if (estGoogle(id)) {
      supprimerGoogle.mutate(idGoogle(id), {
        onSuccess: () => {
          setModale(null);
          notifier('Événement supprimé de Google Calendar');
        },
        onError: (erreur) => notifier(messageErreurGoogle(erreur, "Impossible de supprimer l'événement")),
      });
      return;
    }
    supprimerEvenementEnBase.mutate(id, {
      onSuccess: () => {
        setModale(null);
        notifier('Événement supprimé');
      },
      onError: (erreur) =>
        notifier(messageErreurGoogle(erreur, "Impossible de supprimer l'événement")),
    });
  };

  // Un calendrier vide parce qu'il charge encore ne doit pas se lire comme un
  // périmètre sans rien de prévu.
  const chargementEnCours = enConsultation
    ? calendrierConsulte.isLoading
    : planning.isLoading || evenementsSaisis.isLoading;

  const libellePeriode = libelleDeVue(vue, ancre);
  const uniteVue = vue === 'Année' ? 'Année' : vue === 'Jour' ? 'Jour' : vue === 'Semaine' ? 'Semaine' : 'Mois';

  return (
    <div className="mx-auto max-w-[1500px] space-y-6 p-4 sm:p-6">
      <div className="flex flex-col items-start justify-between gap-4 lg:flex-row lg:items-center">
        <div>
          <h1 className="font-display text-3xl font-extrabold text-codialis-navy">Calendrier</h1>
          {/* Ce que ce calendrier montre est dit par l'API, pas deviné ici : un
              formateur et un pôle n'y voient pas la même chose, et le savoir
              évite de chercher des séances qui ne sont pas de son périmètre. */}
          <p className="mt-1 text-codialis-gray">
            {enConsultation
              ? 'Calendrier consulté en lecture seule.'
              : (planning.data?.explication ??
                'Sessions, évaluations, événements pédagogiques')}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {consultationAutorisee ? (
            <div className="relative">
              <button
                type="button"
                onClick={() => setSelecteurOuvert((ouvert) => !ouvert)}
                aria-expanded={selecteurOuvert}
                title="Ouvrir le calendrier d’un apprenant ou d’un collègue de votre périmètre"
                className="flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-medium hover:bg-gray-50"
              >
                <Icone nom="utilisateurs" className="h-4 w-4" />
                {cible ? cible.nom : 'Mon calendrier'}
                <Icone nom="chevronBas" className="h-4 w-4" />
              </button>
              {selecteurOuvert ? (
                <div className="absolute right-0 z-20 mt-2 w-80 max-w-[calc(100vw-3rem)] rounded-2xl border border-gray-200 bg-white p-3 shadow-card">
                  <label className="sr-only" htmlFor="recherche-calendrier">
                    Rechercher une personne
                  </label>
                  <div className="flex items-center gap-2 rounded-xl border border-gray-200 px-3 py-2">
                    <Icone nom="recherche" className="h-4 w-4 text-codialis-gray" />
                    <input
                      id="recherche-calendrier"
                      type="search"
                      value={rechercheCible}
                      onChange={(evenement) => setRechercheCible(evenement.target.value)}
                      placeholder="Nom ou prénom"
                      className="w-full text-sm outline-none"
                    />
                  </div>
                  <div className="mt-2 flex gap-1 rounded-xl bg-gray-50 p-1">
                    {(
                      [
                        [null, 'Tous'],
                        ['APPRENANT', 'Apprenants'],
                        ['UTILISATEUR', 'Équipe'],
                      ] as [TypePersonne | null, string][]
                    ).map(([valeur, libelle]) => (
                      <button
                        key={libelle}
                        type="button"
                        aria-pressed={vueAnnuaire === valeur}
                        onClick={() => setVueAnnuaire(valeur)}
                        className={`min-w-0 flex-1 rounded-lg px-2 py-1.5 text-xs font-semibold transition ${
                          vueAnnuaire === valeur
                            ? 'bg-codialis-navy text-white'
                            : 'text-codialis-gray hover:bg-white'
                        }`}
                      >
                        {libelle}
                      </button>
                    ))}
                  </div>
                  {sitePerimetre !== TOUS_LES_SITES ? (
                    <p className="mt-2 px-1 text-xs text-codialis-gray">
                      Limité au site {sitePerimetre} — changez le périmètre en haut de l’écran
                      pour élargir.
                    </p>
                  ) : null}
                  <ul className="mt-2 max-h-72 space-y-1 overflow-y-auto">
                    <li>
                      <button
                        type="button"
                        onClick={() => {
                          setCible(null);
                          setSelecteurOuvert(false);
                        }}
                        className="w-full rounded-lg px-3 py-2 text-left text-sm font-medium text-codialis-navy hover:bg-gray-50"
                      >
                        Mon calendrier
                      </button>
                    </li>
                    {personnes.isLoading ? (
                      <li className="px-3 py-2 text-sm text-codialis-gray">Chargement…</li>
                    ) : null}
                    {!personnes.isLoading && (personnes.data?.personnes ?? []).length === 0 ? (
                      <li className="px-3 py-2 text-sm text-codialis-gray">
                        Personne ne correspond dans votre périmètre.
                      </li>
                    ) : null}
                    {(personnes.data?.personnes ?? []).map((personne) => (
                      <li key={`${personne.type}:${personne.id}`}>
                        <button
                          type="button"
                          onClick={() => {
                            setCible(personne);
                            setSelecteurOuvert(false);
                          }}
                          className="w-full rounded-lg px-3 py-2 text-left hover:bg-gray-50"
                        >
                          <span className="block text-sm font-medium text-codialis-navy">
                            {personne.nom}
                          </span>
                          <span className="block text-xs text-codialis-gray">
                            {personne.role}
                            {personne.siteCode ? ` · ${personne.siteCode}` : ''}
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
            </div>
          ) : null}
          <div className="flex gap-1 rounded-xl bg-white p-1 shadow-card">
            {VUES.map((choix) => (
              <button
                key={choix}
                type="button"
                aria-pressed={vue === choix}
                onClick={() => setVue(choix)}
                className={`rounded-lg px-3 py-1.5 text-sm font-medium transition ${
                  vue === choix ? 'bg-codialis-navy text-white' : 'text-codialis-gray hover:bg-gray-50'
                }`}
              >
                {choix}
              </button>
            ))}
          </div>
          {!enConsultation && !connecteGoogle && !etatGoogle.isLoading && (
            <button
              type="button"
              onClick={connecterGoogle}
              disabled={connexionGoogle.isPending}
              title="Autoriser la plateforme à lire et écrire les événements de votre Google Calendar"
              className="flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-medium hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <Icone nom="lien" className="h-4 w-4" />
              {connexionGoogle.isPending ? 'Connexion…' : 'Connecter Google Calendar'}
            </button>
          )}
          {!enConsultation && connecteGoogle && (
            <button
              type="button"
              onClick={() => void synchroniserGoogle()}
              disabled={synchroEnCours}
              title="Recharger les événements du compte Google connecté"
              className="flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-medium hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <Icone nom="actualiser" className={`h-4 w-4 ${synchroEnCours ? 'animate-spin' : ''}`} />
              {synchroEnCours ? 'Synchronisation…' : 'Synchroniser Google'}
            </button>
          )}
          {!enConsultation && abonnementPossible ? (
            <button
              type="button"
              onClick={() => setAbonnementOuvert(true)}
              title="Afficher votre planning dans Google Agenda, Outlook ou Apple, mis à jour automatiquement"
              className="flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-medium hover:bg-gray-50"
            >
              <Icone nom="calendrier" className="h-4 w-4" />
              {abonnementActif ? 'Abonnement agenda' : 'Abonner mon agenda'}
            </button>
          ) : null}
          <button
            type="button"
            onClick={() => {
              telechargerIcs(genererIcs(evenementsAffiches));
              notifier('Calendrier exporté (.ics)');
            }}
            className="flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-medium hover:bg-gray-50"
          >
            <Icone nom="exporter" />
            Exporter (.ics)
          </button>
          {!enConsultation ? (
            <button
              type="button"
              onClick={() => setModale({ mode: 'creation', date: ajourdHuiCle })}
              className="flex items-center gap-2 rounded-xl bg-codialis-navy px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-codialis-deep"
            >
              <Icone nom="plus" />
              Nouvel événement
            </button>
          ) : null}
        </div>
      </div>

      {/* La programmation n'est pas réservée à l'apprenant : le pôle qui consulte
          un planning a les mêmes six affectations à masquer. */}
      {programmationPresente ? (
        <FiltreProgrammation
          actifs={typesJoursActifs}
          compte={compteTypesJours}
          onBasculer={basculerTypeJour}
        />
      ) : null}

      {calendrierApprenantAffiche && !chargementEnCours && !programmationPresente ? (
        <p className="rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm text-codialis-gray">
          Aucune journée n’est programmée pour cette formation et cette session sur la période affichée.
        </p>
      ) : null}

      {erreurPlateforme && !enConsultation ? <MessageErreur message={erreurPlateforme} /> : null}
      {erreurEvenements && !enConsultation ? <MessageErreur message={erreurEvenements} /> : null}
      {erreurConsultation ? <MessageErreur message={erreurConsultation} /> : null}

      {enConsultation && cible ? (
        <div className="flex flex-col gap-2 rounded-2xl border border-codialis-sky/30 bg-codialis-sky/5 p-4 sm:flex-row sm:items-center sm:justify-between">
          <p className="flex items-center gap-2 text-sm text-codialis-navy">
            <Icone nom="oeil" className="h-4 w-4 shrink-0" />
            <span>
              Calendrier de <strong>{cible.nom}</strong> · {cible.role}
              {cible.siteCode ? ` · ${cible.siteCode}` : ''} — lecture seule.
              {calendrierConsulte.data && calendrierConsulte.data.personnelsMasques > 0
                ? ` ${calendrierConsulte.data.personnelsMasques} événement${
                    calendrierConsulte.data.personnelsMasques > 1 ? 's' : ''
                  } personnel${
                    calendrierConsulte.data.personnelsMasques > 1 ? 's' : ''
                  } masqué${calendrierConsulte.data.personnelsMasques > 1 ? 's' : ''}.`
                : ''}
            </span>
          </p>
          <button
            type="button"
            onClick={() => setCible(null)}
            className="shrink-0 rounded-xl border border-gray-200 bg-white px-4 py-2 text-sm font-medium hover:bg-gray-50"
          >
            Revenir à mon calendrier
          </button>
        </div>
      ) : null}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-4">
        <div className="rounded-2xl bg-white p-4 sm:p-5 shadow-card lg:col-span-3 min-w-0">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="font-display text-xl font-bold text-codialis-navy">{libellePeriode}</h2>
            <div className="flex gap-1">
              <button
                type="button"
                aria-label={`${uniteVue} précédent${vue === 'Semaine' || vue === 'Année' ? 'e' : ''}`}
                onClick={() => setAncre((valeur) => ajouterPeriode(vue, valeur, -1))}
                className="rounded-lg p-2 hover:bg-gray-100"
              >
                <Icone nom="chevronGauche" />
              </button>
              <button
                type="button"
                onClick={() => setAncre(new Date())}
                className="rounded-lg px-3 py-2 text-sm font-medium text-codialis-navy hover:bg-gray-100"
              >
                Aujourd'hui
              </button>
              <button
                type="button"
                aria-label={`${uniteVue} suivant${vue === 'Semaine' || vue === 'Année' ? 'e' : ''}`}
                onClick={() => setAncre((valeur) => ajouterPeriode(vue, valeur, 1))}
                className="rounded-lg p-2 hover:bg-gray-100"
              >
                <Icone nom="chevronDroit" />
              </button>
            </div>
          </div>

          {vue === 'Mois' ? (
            <GrilleMois
              ancre={ancre}
              evenementsParJour={evenementsParJour}
              aujourdHuiCle={ajourdHuiCle}
              onOuvrir={ouvrirEvenement}
              onCreer={creerDepuisGrille}
            />
          ) : vue === 'Semaine' ? (
            <GrilleSemaine
              ancre={ancre}
              evenementsParJour={evenementsParJour}
              aujourdHuiCle={ajourdHuiCle}
              onOuvrir={ouvrirEvenement}
              onCreer={creerDepuisGrille}
            />
          ) : vue === 'Jour' ? (
            <VueJour
              ancre={ancre}
              evenementsParJour={evenementsParJour}
              onOuvrir={ouvrirEvenement}
              onCreer={creerDepuisGrille}
            />
          ) : (
            <VueAnnee
              ancre={ancre}
              evenementsParJour={evenementsParJour}
              aujourdHuiCle={ajourdHuiCle}
              onOuvrirMois={(mois) => {
                setAncre(new Date(ancre.getFullYear(), mois, 1));
                setVue('Mois');
              }}
            />
          )}
        </div>

        <div className="space-y-6">
          <div className="rounded-2xl bg-white p-4 sm:p-5 shadow-card">
            <div className="mb-2 text-xs font-bold uppercase tracking-wider text-codialis-gray">Aujourd'hui</div>
            <h2 className="font-display mb-4 text-lg font-bold text-codialis-navy">
              {evenementsAujourdHui.length} événement{evenementsAujourdHui.length > 1 ? 's' : ''}
            </h2>
            <div className="space-y-3">
              {chargementEnCours ? (
                <p className="text-sm text-codialis-gray">Chargement du planning…</p>
              ) : evenementsAujourdHui.length === 0 ? (
                <p className="text-sm text-codialis-gray">Aucun événement aujourd'hui sur ce périmètre.</p>
              ) : (
                evenementsAujourdHui.map((evenement) => (
                  <button
                    key={evenement.id}
                    type="button"
                    onClick={() => ouvrirEvenement(evenement)}
                    style={
                      evenement.typeJour
                        ? { borderLeftColor: STYLES_JOUR[evenement.typeJour].hex }
                        : undefined
                    }
                    className={`block w-full border-l-4 py-1 pl-3 text-left ${
                      evenement.typeJour ? '' : BORDURE_PAR_CATEGORIE[evenement.categorie]
                    }`}
                  >
                    <div className="text-xs text-codialis-gray">
                      {evenement.heureDebut ? `${evenement.heureDebut}${evenement.heureFin ? ` · ${evenement.heureFin}` : ''}` : 'Toute la journée'}
                    </div>
                    <div className="text-sm font-medium text-codialis-navy">{evenement.titre}</div>
                    <div className="text-xs text-codialis-gray">{evenement.lieu ?? '—'}</div>
                  </button>
                ))
              )}
            </div>
          </div>

          <div className="rounded-2xl bg-white p-4 sm:p-5 shadow-card">
            <div className="mb-3 text-xs font-bold uppercase tracking-wider text-codialis-gray">
              Mes calendriers
            </div>
            <div className="space-y-2">
              {/* Ces cases ne couvrent jamais les journées programmées : celles-ci
                  ont leurs six pastilles en haut d'écran, et les deux jeux se
                  décochent indépendamment. */}
              {CALENDRIERS.filter(
                ([libelle]) => libelle !== 'Google Calendar' || connecteGoogle,
              ).map(([libelle, accent, puce]) => (
                <label key={libelle} className="flex cursor-pointer items-center gap-2.5">
                  <input
                    type="checkbox"
                    checked={actifs[libelle]}
                    onChange={() => basculer(libelle)}
                    className={`h-4 w-4 rounded ${accent}`}
                  />
                  <span className={`h-3 w-3 rounded ${puce}`} />
                  <span className="text-sm text-codialis-navy">{libelle}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="rounded-2xl bg-codialis-navy p-4 sm:p-5 text-white">
            <div className="mb-2 text-xs font-bold uppercase tracking-wider text-codialis-mint">Compatible avec</div>
            {!enConsultation && abonnementPossible ? (
              <div className="mb-4 border-b border-white/10 pb-4">
                <p className="mb-3 text-xs text-white/70">
                  {abonnementActif
                    ? 'Votre agenda est abonné à votre planning : il s’y met à jour tout seul.'
                    : 'Abonnez votre agenda Google, Outlook ou Apple à votre planning : il s’y mettra à jour tout seul.'}
                </p>
                <BoutonMint onClick={() => setAbonnementOuvert(true)}>
                  {abonnementActif ? 'Gérer l’abonnement' : 'Abonner mon agenda'}
                </BoutonMint>
              </div>
            ) : null}
            {connecteGoogle ? (
              <>
                <p className="mb-3 text-xs text-white/70">
                  Google Calendar connecté{etatGoogle.data?.email ? ` (${etatGoogle.data.email})` : ''}. Les
                  événements ci-dessus sont ceux de ce compte, synchronisés automatiquement.
                </p>
                {evenementsGoogle.dataUpdatedAt > 0 && (
                  <p className="mb-3 text-xs text-white/50">
                    Dernière synchronisation : {heureLocale(evenementsGoogle.dataUpdatedAt)}
                  </p>
                )}
                <div className="flex flex-wrap gap-2">
                  <BoutonMint onClick={() => void synchroniserGoogle()} disabled={synchroEnCours}>
                    {synchroEnCours ? 'Synchronisation…' : 'Synchroniser maintenant'}
                  </BoutonMint>
                  <BoutonSecondaire
                    onClick={() =>
                      deconnexionGoogle.mutate(undefined, {
                        onSuccess: () => notifier('Google Calendar déconnecté'),
                        onError: (erreur) =>
                          notifier(messageErreurGoogle(erreur, 'Impossible de déconnecter Google Calendar')),
                      })
                    }
                  >
                    {deconnexionGoogle.isPending ? 'Déconnexion…' : 'Déconnecter'}
                  </BoutonSecondaire>
                </div>
              </>
            ) : (
              <>
                <p className="mb-3 text-xs text-white/70">
                  Export .ics importable manuellement, ou connexion directe à votre compte Google Calendar pour
                  voir et écrire ses événements ici.
                </p>
                <BoutonPrincipal onClick={connecterGoogle} disabled={connexionGoogle.isPending}>
                  {connexionGoogle.isPending ? 'Connexion…' : 'Connecter Google Calendar'}
                </BoutonPrincipal>
              </>
            )}
            <div className="mt-3 flex flex-wrap gap-2">
              {['Google Calendar', 'Outlook 365', 'Apple Calendar'].map((service) => (
                <span key={service} className="rounded bg-white/10 px-2.5 py-1 text-xs">
                  {service}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="rounded-2xl bg-white p-4 sm:p-5 shadow-card">
        <h2 className="font-display mb-4 text-lg font-bold text-codialis-navy">Prochaines échéances</h2>
        {chargementEnCours ? (
          <p className="text-sm text-codialis-gray">Chargement du planning…</p>
        ) : prochainesEcheances.length === 0 ? (
          <p className="text-sm text-codialis-gray">Aucune échéance à venir sur ce périmètre.</p>
        ) : (
          <div>
            <div className="relative overflow-x-auto">
              <table className="w-full text-sm">
              <thead className="border-b border-gray-100">
                <tr>
                  {['Date', 'Heure', 'Événement', 'Calendrier', 'Échéance'].map((colonne) => (
                    <th
                      key={colonne}
                      className="px-3 py-2 text-left text-[11px] font-bold uppercase tracking-wider text-codialis-gray"
                    >
                      {colonne}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {prochainesEcheancesPage.map((evenement) => (
                  <tr
                    key={evenement.id}
                    onClick={() => ouvrirEvenement(evenement)}
                    className="cursor-pointer transition hover:bg-gray-50"
                  >
                    <td className="whitespace-nowrap px-3 py-2.5 text-codialis-navy">
                      {new Date(`${evenement.date}T00:00:00+04:00`).toLocaleDateString('fr-FR', {
                        day: '2-digit',
                        month: 'short',
                      })}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2.5 text-xs text-codialis-gray">
                      {evenement.heureDebut
                        ? `${evenement.heureDebut}${evenement.heureFin ? ` · ${evenement.heureFin}` : ''}`
                        : 'Journée entière'}
                    </td>
                    <td className="px-3 py-2.5 font-medium text-codialis-navy">{evenement.titre}</td>
                    <td className="px-3 py-2.5">
                      <span
                        className={`whitespace-nowrap rounded px-2 py-0.5 text-[10px] font-bold ${classeEvenement(evenement)}`}
                      >
                        {libelleCalendrier(evenement)}
                      </span>
                    </td>
                    <td className="whitespace-nowrap px-3 py-2.5 text-xs font-semibold text-codialis-navy">
                      {libelleEcheance(evenement, maintenant)}
                    </td>
                  </tr>
                ))}
              </tbody>
              </table>
            </div>
            <Pagination
              page={pageEcheancesCourante}
              pages={pagesEcheances}
              total={prochainesEcheances.length}
              affiches={prochainesEcheancesPage.length}
              taillePage={TAILLE_PAGE_ECHEANCES}
              onPage={setPageEcheances}
              uniteLabel="échéance"
            />
          </div>
        )}
      </div>

      {abonnementAgenda.data ? (
        <Modale
          ouverte={abonnementOuvert}
          onFermer={() => setAbonnementOuvert(false)}
          titre="Abonner mon agenda"
          sousTitre={abonnementAgenda.data.nomCalendrier}
          largeur="max-w-xl"
        >
          <AbonnementAgenda
            etat={abonnementAgenda.data}
            espace={planning.data?.perimetre === 'INTERVENANT' ? 'formateur' : 'apprenant'}
          />
        </Modale>
      ) : null}

      <Modale
        ouverte={modale !== null}
        onFermer={() => setModale(null)}
        titre={
          modale?.mode === 'detail'
            ? modale.evenement.titre
            : modale?.mode === 'edition'
              ? "Modifier l'événement"
              : 'Nouvel événement'
        }
        sousTitre={modale?.mode === 'creation' ? `Le ${modale.date}` : undefined}
        largeur="max-w-lg"
      >
        {modale?.mode === 'detail' ? (
          <DetailEvenement evenement={modale.evenement} onFermer={() => setModale(null)} />
        ) : modale && (
          <FormulaireEvenement
            valeurInitiale={modale.mode === 'edition' ? modale.evenement : { date: modale.date }}
            modeEdition={modale.mode === 'edition'}
            categoriesSaisissables={categoriesSaisissables}
            googleDisponible={connecteGoogle}
            verrouilleSurGoogle={modale.mode === 'edition' && estGoogle(modale.evenement.id)}
            enregistrementEnCours={
              creerEvenementEnBase.isPending || modifierEvenementEnBase.isPending
            }
            onAnnuler={() => setModale(null)}
            onEnregistrer={(entree) =>
              modale.mode === 'edition' ? modifierEvenement(modale.evenement.id, entree) : ajouterEvenement(entree)
            }
            onSupprimer={modale.mode === 'edition' ? () => supprimerEvenement(modale.evenement.id) : undefined}
          />
        )}
      </Modale>
    </div>
  );
}

function FormulaireEvenement({
  valeurInitiale,
  modeEdition,
  categoriesSaisissables,
  googleDisponible = false,
  verrouilleSurGoogle = false,
  enregistrementEnCours = false,
  onAnnuler,
  onEnregistrer,
  onSupprimer,
}: {
  valeurInitiale: Partial<EvenementCalendrier> & { date: string };
  modeEdition: boolean;
  /** Catégories que l'API accepte de ce profil. L'apprenant n'en a qu'une. */
  categoriesSaisissables: CategorieCalendrier[];
  /** Compte Google relié : « Google Calendar » devient une destination possible. */
  googleDisponible?: boolean;
  /** Événement déjà chez Google : on ne le déplace pas d'un calendrier à l'autre. */
  verrouilleSurGoogle?: boolean;
  enregistrementEnCours?: boolean;
  onAnnuler: () => void;
  onEnregistrer: (entree: Omit<EvenementCalendrier, 'id'>) => void;
  onSupprimer?: () => void;
}): JSX.Element {
  // Destinations réellement proposées : ce que l'API accepte, plus Google quand
  // le compte est relié. Un profil qui n'a qu'une catégorie ne voit pas de
  // menu déroulant à un seul choix, mais le nom de ce calendrier.
  const destinations: CategorieCalendrier[] = [
    ...categoriesSaisissables,
    ...(googleDisponible ? (['Google Calendar'] as CategorieCalendrier[]) : []),
  ];

  const [titre, setTitre] = useState(valeurInitiale.titre ?? '');
  const [date, setDate] = useState(valeurInitiale.date);
  const [categorie, setCategorie] = useState<CategorieCalendrier>(() => {
    if (verrouilleSurGoogle) return 'Google Calendar';
    if (valeurInitiale.categorie) return valeurInitiale.categorie;
    // « Personnel » est la seule catégorie ouverte à tous les profils : c'est
    // le défaut le plus sûr pour une création, plutôt que la première
    // catégorie de la liste (« Mes formations » pour un profil non-apprenant),
    // qui créerait silencieusement l'événement dans le mauvais calendrier.
    return destinations.includes('Personnel') ? 'Personnel' : (destinations[0] ?? 'Personnel');
  });
  const [heureDebut, setHeureDebut] = useState(valeurInitiale.heureDebut ?? '');
  const [heureFin, setHeureFin] = useState(valeurInitiale.heureFin ?? '');
  const [lieu, setLieu] = useState(valeurInitiale.lieu ?? '');
  const [erreur, setErreur] = useState<string | null>(null);
  const [confirmationSuppression, setConfirmationSuppression] = useState(false);

  const complet = titre.trim().length >= 3 && date.length > 0;

  if (confirmationSuppression) {
    return (
      <div className="space-y-4 p-4 sm:p-6">
        <p className="text-sm text-codialis-navy">
          Supprimer définitivement « {valeurInitiale.titre} » ? Cette action est irréversible.
        </p>
        <div className="flex justify-end gap-2">
          <BoutonSecondaire onClick={() => setConfirmationSuppression(false)}>Annuler</BoutonSecondaire>
          <button
            type="button"
            onClick={onSupprimer}
            className="rounded-xl bg-red-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-red-700"
          >
            Confirmer la suppression
          </button>
        </div>
      </div>
    );
  }

  return (
    <form
      className="space-y-4 p-4 sm:p-6"
      onSubmit={(evenement) => {
        evenement.preventDefault();
        if (titre.trim().length < 3) {
          setErreur('Le titre doit comporter au moins 3 caractères.');
          return;
        }
        if (!date) {
          setErreur('La date est obligatoire.');
          return;
        }
        setErreur(null);
        onEnregistrer({
          titre: titre.trim(),
          date,
          categorie,
          ...(heureDebut ? { heureDebut } : {}),
          ...(heureFin ? { heureFin } : {}),
          ...(lieu.trim() ? { lieu: lieu.trim() } : {}),
        });
      }}
    >
      <div>
        <label htmlFor="calendrier-titre" className="mb-1 block text-[11px] font-bold uppercase tracking-wide text-codialis-navy">Titre</label>
        <input id="calendrier-titre"
          value={titre}
          onChange={(evenement) => setTitre(evenement.target.value)}
          placeholder="Ex. Point équipe formateurs"
          className="w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm"
        />
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor="calendrier-date" className="mb-1 block text-[11px] font-bold uppercase tracking-wide text-codialis-navy">Date</label>
          <input id="calendrier-date"
            type="date"
            value={date}
            onChange={(evenement) => setDate(evenement.target.value)}
            className="w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm"
          />
        </div>
        <div>
          <div
            id="calendrier-destination"
            className="mb-1 block text-[11px] font-bold uppercase tracking-wide text-codialis-navy"
          >
            Calendrier
          </div>
          {verrouilleSurGoogle || destinations.length <= 1 ? (
            <div className="flex h-[42px] items-center rounded-xl border border-gray-200 bg-gray-100 px-3 text-sm text-codialis-gray">
              {categorie}
            </div>
          ) : (
            <select
              aria-labelledby="calendrier-destination"
              value={categorie}
              onChange={(evenement) => setCategorie(evenement.target.value as CategorieCalendrier)}
              className="w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm"
            >
              {destinations.map((libelle) => (
                <option key={libelle} value={libelle}>
                  {libelle}
                </option>
              ))}
            </select>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor="calendrier-heure-de-debut" className="mb-1 block text-[11px] font-bold uppercase tracking-wide text-codialis-navy">
            Heure de début
          </label>
          <input id="calendrier-heure-de-debut"
            type="time"
            value={heureDebut}
            onChange={(evenement) => setHeureDebut(evenement.target.value)}
            className="w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm"
          />
        </div>
        <div>
          <label htmlFor="calendrier-heure-de-fin" className="mb-1 block text-[11px] font-bold uppercase tracking-wide text-codialis-navy">
            Heure de fin
          </label>
          <input id="calendrier-heure-de-fin"
            type="time"
            value={heureFin}
            onChange={(evenement) => setHeureFin(evenement.target.value)}
            className="w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm"
          />
        </div>
      </div>

      <div>
        <label htmlFor="calendrier-lieu" className="mb-1 block text-[11px] font-bold uppercase tracking-wide text-codialis-navy">Lieu</label>
        <input id="calendrier-lieu"
          value={lieu}
          onChange={(evenement) => setLieu(evenement.target.value)}
          placeholder="Ex. Visio Teams, salle B12…"
          className="w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm"
        />
      </div>

      {erreur && <MessageErreur message={erreur} />}

      <div className="flex items-center justify-between gap-2 pt-2">
        {modeEdition && onSupprimer ? (
          <button
            type="button"
            onClick={() => setConfirmationSuppression(true)}
            className="rounded-xl border border-red-200 px-4 py-2.5 text-sm font-semibold text-red-600 transition hover:bg-red-50"
          >
            Supprimer
          </button>
        ) : (
          <span />
        )}
        <div className="flex gap-2">
          <BoutonSecondaire onClick={onAnnuler}>Annuler</BoutonSecondaire>
          <BoutonPrincipal type="submit" disabled={!complet || enregistrementEnCours}>
            {enregistrementEnCours
              ? 'Enregistrement…'
              : modeEdition
                ? 'Enregistrer'
                : "Ajouter l'événement"}
          </BoutonPrincipal>
        </div>
      </div>
    </form>
  );
}

interface ProprietesVue {
  ancre: Date;
  evenementsParJour: Map<string, EvenementCalendrier[]>;
  onOuvrir: (evenement: EvenementCalendrier) => void;
  /** `null` en consultation : le calendrier d'un tiers ne s'écrit pas. */
  onCreer: ((date: string) => void) | null;
}

/** Pastille d'événement, identique dans les quatre vues. */
function Pastille({
  evenement,
  onOuvrir,
  compacte,
}: {
  evenement: EvenementCalendrier;
  onOuvrir: (evenement: EvenementCalendrier) => void;
  compacte?: boolean;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={(clic) => {
        clic.stopPropagation();
        onOuvrir(evenement);
      }}
      className={`block w-full truncate rounded px-1.5 py-0.5 text-left ${
        compacte ? 'text-[10px]' : 'text-xs'
      } ${classeEvenement(evenement)}`}
    >
      {evenement.heureDebut && !compacte ? `${evenement.heureDebut} · ` : ''}
      {evenement.titre}
    </button>
  );
}

/**
 * Clic dans une case : un raccourci à la souris pour créer un événement ce
 * jour-là. La case n'est pas un bouton — elle contient ceux des événements, et
 * un bouton ne s'emboîte pas dans un autre (RGAA 7.1, TF-341) : au clavier, la
 * création passe par le numéro du jour (`NumeroJour`), qui en est un.
 */
function casePourCreation(
  dateCle: string | null,
  onCreer: ((date: string) => void) | null,
): Pick<JSX.IntrinsicElements['div'], 'onClick'> {
  if (!dateCle || !onCreer) return {};
  return { onClick: () => onCreer(dateCle) };
}

/** Numéro du jour ; bouton « Ajouter un événement » quand la création est ouverte. */
function NumeroJour({
  dateCle,
  onCreer,
  className,
  children,
}: {
  dateCle: string | null;
  onCreer: ((date: string) => void) | null;
  className: string;
  children: React.ReactNode;
}): JSX.Element {
  if (!dateCle || !onCreer) return <div className={className}>{children}</div>;
  return (
    <button
      type="button"
      aria-label={`Ajouter un événement le ${dateCle}`}
      onClick={(clic) => {
        // La case l'aurait créé aussi : un seul événement par clic.
        clic.stopPropagation();
        onCreer(dateCle);
      }}
      className={className}
    >
      {children}
    </button>
  );
}

function GrilleMois({
  ancre,
  evenementsParJour,
  aujourdHuiCle,
  onOuvrir,
  onCreer,
}: ProprietesVue & { aujourdHuiCle: string }): JSX.Element {
  const mois = debutDuMois(ancre);
  const cases = useMemo(() => {
    const offset = decalageSemaine(mois);
    const total = nombreJoursDuMois(mois);
    return Array.from({ length: 42 }, (_, index) => {
      const numero = index - offset + 1;
      const valide = numero >= 1 && numero <= total;
      const dateCle = valide
        ? `${mois.getFullYear()}-${pad(mois.getMonth() + 1)}-${pad(numero)}`
        : null;
      return { numero, valide, dateCle, aujourdHui: dateCle === aujourdHuiCle };
    });
  }, [mois, aujourdHuiCle]);

  return (
    // La grille ne se comprime pas sous la largeur d'une tablette en portrait :
    // elle défile dans son propre conteneur plutôt que de pousser la page à
    // défiler horizontalement.
    <div className="relative overflow-x-auto">
      <div className="grid min-w-[640px] grid-cols-7 gap-px overflow-hidden rounded-xl bg-gray-100">
        {JOURS.map((jour) => (
          <div
            key={jour}
            className="bg-gray-50 px-2 py-2 text-center text-[11px] font-bold uppercase tracking-wide text-codialis-gray"
          >
            {jour}
          </div>
        ))}
        {cases.map((jour, index) => (
          <div
            key={index}
            {...casePourCreation(jour.dateCle, onCreer)}
            className={`calendar-cell min-h-[84px] border border-gray-100 p-2 transition ${
              jour.valide ? `bg-white ${onCreer ? 'cursor-pointer hover:bg-gray-50' : ''}` : 'bg-gray-50'
            } ${jour.aujourdHui ? 'ring-2 ring-codialis-mint' : ''}`}
          >
            <NumeroJour
              dateCle={jour.dateCle}
              onCreer={onCreer}
              className={`block text-left text-xs font-medium ${
                jour.aujourdHui
                  ? 'text-codialis-mint-clair'
                  : jour.valide
                    ? 'text-codialis-navy'
                    : 'text-codialis-gray/40'
              }`}
            >
              {jour.valide ? jour.numero : ''}
            </NumeroJour>
            <div className="mt-1 space-y-1">
              {jour.valide &&
                jour.dateCle &&
                (evenementsParJour.get(jour.dateCle) ?? []).map((evenement) => (
                  <Pastille key={evenement.id} evenement={evenement} onOuvrir={onOuvrir} compacte />
                ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function GrilleSemaine({
  ancre,
  evenementsParJour,
  aujourdHuiCle,
  onOuvrir,
  onCreer,
}: ProprietesVue & { aujourdHuiCle: string }): JSX.Element {
  const lundi = debutSemaine(ancre);
  const jours = Array.from({ length: 7 }, (_, index) => {
    const date = ajouterJoursLocaux(lundi, index);
    const dateCle = versCleJour(date);
    return {
      dateCle,
      libelle: JOURS[index] as string,
      libelleLong: majuscule(date.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })),
      numero: date.getDate(),
      aujourdHui: dateCle === aujourdHuiCle,
    };
  });

  return (
    <>
      {/* Sous la largeur d'une tablette, sept colonnes ne se lisent plus : la
          semaine devient une liste de jours, sans défilement horizontal. */}
      <ul className="space-y-2 md:hidden">
        {jours.map((jour) => {
          const evenements = evenementsParJour.get(jour.dateCle) ?? [];
          return (
            <li
              key={`liste-${jour.dateCle}`}
              className={`rounded-xl border p-3 ${
                jour.aujourdHui ? 'border-codialis-mint ring-1 ring-codialis-mint' : 'border-gray-100'
              }`}
            >
              <div className="mb-2 flex items-center justify-between gap-2">
                <span
                  className={`min-w-0 truncate text-sm font-bold ${
                    jour.aujourdHui ? 'text-codialis-mint-clair' : 'text-codialis-navy'
                  }`}
                >
                  {jour.libelleLong}
                </span>
                {onCreer ? (
                  <button
                    type="button"
                    onClick={() => onCreer(jour.dateCle)}
                    aria-label={`Ajouter un événement le ${jour.dateCle}`}
                    className="shrink-0 rounded-lg p-1.5 text-codialis-gray hover:bg-gray-100"
                  >
                    <Icone nom="plus" className="h-4 w-4" />
                  </button>
                ) : null}
              </div>
              {evenements.length === 0 ? (
                <p className="text-xs text-codialis-gray">Rien de prévu</p>
              ) : (
                <div className="space-y-1">
                  {evenements.map((evenement) => (
                    <Pastille key={evenement.id} evenement={evenement} onOuvrir={onOuvrir} />
                  ))}
                </div>
              )}
            </li>
          );
        })}
      </ul>
      <div className="relative hidden overflow-x-auto md:block">
        <div className="grid min-w-[640px] grid-cols-7 gap-px overflow-hidden rounded-xl bg-gray-100">
          {jours.map((jour) => (
            <div
              key={`entete-${jour.dateCle}`}
              className={`px-2 py-2 text-center ${jour.aujourdHui ? 'bg-codialis-mint/15' : 'bg-gray-50'}`}
            >
              <div className="text-[11px] font-bold uppercase tracking-wide text-codialis-gray">
                {jour.libelle}
              </div>
              <NumeroJour
                dateCle={jour.dateCle}
                onCreer={onCreer}
                className={`mx-auto block text-sm font-bold ${
                  jour.aujourdHui ? 'text-codialis-mint-clair' : 'text-codialis-navy'
                }`}
              >
                {jour.numero}
              </NumeroJour>
            </div>
          ))}
          {jours.map((jour) => (
            <div
              key={jour.dateCle}
              {...casePourCreation(jour.dateCle, onCreer)}
              className={`calendar-cell min-h-[260px] space-y-1 border border-gray-100 bg-white p-2 transition ${
                onCreer ? 'cursor-pointer hover:bg-gray-50' : ''
              } ${jour.aujourdHui ? 'ring-2 ring-codialis-mint' : ''}`}
            >
              {(evenementsParJour.get(jour.dateCle) ?? []).map((evenement) => (
                <Pastille key={evenement.id} evenement={evenement} onOuvrir={onOuvrir} />
              ))}
            </div>
          ))}
        </div>
      </div>
    </>
  );
}

function VueJour({ ancre, evenementsParJour, onOuvrir, onCreer }: ProprietesVue): JSX.Element {
  const dateCle = versCleJour(ancre);
  const evenements = evenementsParJour.get(dateCle) ?? [];

  return (
    <div className="space-y-3">
      {evenements.length === 0 ? (
        <p className="rounded-xl bg-gray-50 px-4 py-8 text-center text-sm text-codialis-gray">
          Aucun événement ce jour-là.
        </p>
      ) : (
        <ul className="space-y-2">
          {evenements.map((evenement) => (
            <li key={evenement.id}>
              <button
                type="button"
                onClick={() => onOuvrir(evenement)}
                style={
                  evenement.typeJour
                    ? { borderLeftColor: STYLES_JOUR[evenement.typeJour].hex }
                    : undefined
                }
                className={`flex w-full items-start gap-4 rounded-xl border-l-4 bg-white px-4 py-3 text-left shadow-sm transition hover:bg-gray-50 ${
                  evenement.typeJour ? '' : BORDURE_PAR_CATEGORIE[evenement.categorie]
                }`}
              >
                <span className="w-24 shrink-0 text-sm font-semibold text-codialis-navy">
                  {evenement.heureDebut
                    ? `${evenement.heureDebut}${evenement.heureFin ? ` – ${evenement.heureFin}` : ''}`
                    : 'Journée'}
                </span>
                <span className="min-w-0">
                  <span className="block truncate text-sm font-medium text-codialis-navy">
                    {evenement.titre}
                  </span>
                  <span className="block text-xs text-codialis-gray">
                    {libelleCalendrier(evenement)}
                    {evenement.lieu ? ` · ${evenement.lieu}` : ''}
                  </span>
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
      {onCreer ? (
        <button
          type="button"
          onClick={() => onCreer(dateCle)}
          className="flex w-full items-center justify-center gap-2 rounded-xl border border-dashed border-gray-300 px-4 py-3 text-sm font-medium text-codialis-gray transition hover:bg-gray-50"
        >
          <Icone nom="plus" className="h-4 w-4" />
          Ajouter un événement ce jour-là
        </button>
      ) : null}
    </div>
  );
}

/**
 * Vue annuelle : douze mois, chacun avec le nombre d'événements de la période
 * chargée. Cliquer sur un mois bascule en vue Mois plutôt que d'entasser des
 * pastilles illisibles dans une case de quelques pixels.
 */
function VueAnnee({
  ancre,
  evenementsParJour,
  aujourdHuiCle,
  onOuvrirMois,
}: {
  ancre: Date;
  evenementsParJour: Map<string, EvenementCalendrier[]>;
  aujourdHuiCle: string;
  onOuvrirMois: (mois: number) => void;
}): JSX.Element {
  const annee = ancre.getFullYear();
  const moisCourant = aujourdHuiCle.startsWith(`${annee}-`)
    ? Number(aujourdHuiCle.slice(5, 7)) - 1
    : null;

  const comptes = useMemo(() => {
    const table = new Array<number>(12).fill(0);
    for (const [dateCle, evenements] of evenementsParJour) {
      if (!dateCle.startsWith(`${annee}-`)) continue;
      const mois = Number(dateCle.slice(5, 7)) - 1;
      table[mois] = (table[mois] ?? 0) + evenements.length;
    }
    return table;
  }, [evenementsParJour, annee]);

  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
      {comptes.map((compte, mois) => (
        <button
          key={mois}
          type="button"
          onClick={() => onOuvrirMois(mois)}
          className={`rounded-xl border border-gray-100 bg-white p-4 text-left transition hover:bg-gray-50 ${
            mois === moisCourant ? 'ring-2 ring-codialis-mint' : ''
          }`}
        >
          <div className="font-display text-sm font-bold capitalize text-codialis-navy">
            {new Date(annee, mois, 1).toLocaleDateString('fr-FR', { month: 'long' })}
          </div>
          <div className="mt-1 text-xs text-codialis-gray">
            {compte === 0 ? 'Aucun événement' : `${compte} événement${compte > 1 ? 's' : ''}`}
          </div>
        </button>
      ))}
    </div>
  );
}

/**
 * Détail en lecture seule d'une ligne du planning ou du calendrier consulté.
 *
 * On n'y montre que ce que l'API a rendu : aucune donnée personnelle
 * d'apprenant n'y figure côté formateur ou pôle — le nombre d'inscrits suffit à
 * préparer une séance, la liste nominative se lit sur l'émargement, écran fait
 * pour ça et tracé comme tel.
 */
/**
 * Même ordre, mêmes libellés et mêmes couleurs que la programmation de
 * `/adm-formations`. La grille permet deux colonnes sur mobile et se déroule
 * naturellement sur tablette et bureau.
 */
/**
 * Programmation de la formation : la légende **est** le filtre.
 *
 * Les six affectations de « Programmation du calendrier » (`/adm-formations`)
 * se décochent une par une. Elles sont volontairement **hors** des cases
 * « Mes calendriers » : une journée programmée n'est pas une séance, et la
 * ranger sous « Mes formations » revenait à ne pouvoir masquer ni les unes ni
 * les autres séparément — c'était le défaut de la version précédente, où les
 * six pastilles remplaçaient les catégories au lieu de s'y ajouter.
 *
 * Chaque pastille annonce son volume sur la période affichée : un filtre qui ne
 * dit pas ce qu'il cache se coche à l'aveugle. Un type absent de la période est
 * rendu désactivé plutôt que retiré, pour que la légende garde ses six couleurs.
 */
function FiltreProgrammation({
  actifs,
  compte,
  onBasculer,
}: {
  actifs: Record<TypeJour, boolean>;
  compte: Record<TypeJour, number>;
  onBasculer: (type: TypeJour) => void;
}): JSX.Element {
  return (
    <section className="rounded-2xl border border-gray-100 bg-white p-4 shadow-card">
      <h2 className="mb-3 text-xs font-bold uppercase tracking-wider text-codialis-gray">
        Programmation de la formation
      </h2>
      <ul className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap">
        {ORDRE_TYPES.map((type) => {
          const present = compte[type] > 0;
          const actif = actifs[type];
          return (
            <li key={type} className="min-w-0">
              <button
                type="button"
                aria-pressed={actif}
                disabled={!present}
                onClick={() => onBasculer(type)}
                title={
                  present
                    ? actif
                      ? `Masquer « ${STYLES_JOUR[type].libelle} »`
                      : `Afficher « ${STYLES_JOUR[type].libelle} »`
                    : 'Aucune journée de ce type sur la période affichée'
                }
                className={`inline-flex w-full min-w-0 items-center justify-center gap-1.5 rounded-full px-3 py-1.5 text-center text-xs font-semibold transition sm:w-auto sm:justify-start [@media(pointer:coarse)]:min-h-[40px] ${
                  actif && present
                    ? STYLES_JOUR[type].pastille
                    : 'bg-white text-codialis-gray ring-1 ring-gray-200'
                } ${present ? 'hover:opacity-90' : 'cursor-not-allowed opacity-50'}`}
              >
                <span
                  aria-hidden="true"
                  className={`h-2.5 w-2.5 shrink-0 rounded-full ${
                    actif && present ? 'bg-current' : `${STYLES_JOUR[type].pastille} opacity-60`
                  }`}
                />
                <span className="truncate">{STYLES_JOUR[type].libelle}</span>
                <span className="texte-attenue shrink-0 tabular-nums opacity-70">{compte[type]}</span>
              </button>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

function DetailEvenement({
  evenement,
  onFermer,
}: {
  evenement: EvenementCalendrier;
  onFermer: () => void;
}): JSX.Element {
  const detail = evenement.detail ?? {};
  const lignes: [string, string][] = [
    [
      'Horaires',
      evenement.heureDebut
        ? `${evenement.heureDebut}${evenement.heureFin ? ` – ${evenement.heureFin}` : ''}`
        : 'Journée entière',
    ],
    ...(detail.formation ? ([['Formation', detail.formation]] as [string, string][]) : []),
    ...(detail.groupe ? ([['Groupe', detail.groupe]] as [string, string][]) : []),
    ...(detail.formateur ? ([['Formateur', detail.formateur]] as [string, string][]) : []),
    ...(detail.entreprise ? ([['Entreprise', detail.entreprise]] as [string, string][]) : []),
    ...(detail.site ? ([['Antenne', detail.site]] as [string, string][]) : []),
    ...(detail.salle ? ([['Salle / lieu', detail.salle]] as [string, string][]) : []),
    ...(!detail.salle && evenement.lieu
      ? ([['Lieu', evenement.lieu]] as [string, string][])
      : []),
    ...(detail.type ? ([['Modalité', detail.type]] as [string, string][]) : []),
    ...(detail.statut ? ([['Statut', detail.statut]] as [string, string][]) : []),
    ...(detail.inscrits !== undefined
      ? ([['Inscrits', String(detail.inscrits)]] as [string, string][])
      : []),
  ];

  return (
    <div className="space-y-4 p-4 sm:p-6">
      <div className="flex flex-wrap items-center gap-2">
        <span
          className={`rounded px-2 py-0.5 text-[10px] font-bold ${classeEvenement(evenement)}`}
        >
          {libelleCalendrier(evenement)}
        </span>
        <span className="text-sm text-codialis-gray">
          {new Date(`${evenement.date}T00:00:00+04:00`).toLocaleDateString('fr-FR', {
            weekday: 'long',
            day: '2-digit',
            month: 'long',
            year: 'numeric',
          })}
        </span>
      </div>

      <dl className="divide-y divide-gray-100 rounded-xl border border-gray-100">
        {lignes.map(([libelle, valeur]) => (
          <div key={libelle} className="flex flex-col gap-0.5 px-3 py-2 sm:flex-row sm:gap-4">
            <dt className="text-xs font-bold uppercase tracking-wider text-codialis-gray sm:w-40 sm:shrink-0">
              {libelle}
            </dt>
            <dd className="min-w-0 text-sm text-codialis-navy">{valeur}</dd>
          </div>
        ))}
      </dl>

      <p className="text-xs text-codialis-gray">
        Planifié par l’organisme : cet élément se modifie dans l’écran dont il relève, pas
        depuis le calendrier.
      </p>

      <div className="flex justify-end">
        <BoutonSecondaire onClick={onFermer}>Fermer</BoutonSecondaire>
      </div>
    </div>
  );
}
